import React, { useMemo } from 'react';
import { motion } from 'framer-motion';
import { 
  TrendingUp,
  Calendar as CalendarIcon,
  FileText, 
  Activity,
  FileCode,
  Bell,
  HardDrive,
  Smartphone
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import { useNotes } from '../../contexts/NotesContext';
import { useBreakReminder } from '../../contexts/BreakReminderContext';
import { useNavigate } from 'react-router-dom';
import { getIntlLocale } from '../../utils/dateLocale';

const StatisticsPage = () => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation('common');
  const { user, isAuthenticated } = useAuth();
  const { notes } = useNotes();
  const { breaks } = useBreakReminder();
  const intlLocale = getIntlLocale(i18n.language);

  // Calculate statistics
  const stats = useMemo(() => {
    const now = new Date();
    const weekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
    const monthAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);

    const notesThisWeek = notes.filter(note => {
      const created = new Date(note.createdAt || note.created_at || 0);
      return created >= weekAgo;
    }).length;

    const lastNoteDate = notes.length > 0 
      ? new Date(Math.max(...notes.map(n => new Date(n.createdAt || n.created_at || 0).getTime())))
      : null;

    const largestNote = notes.reduce((max, note) => {
      const size = (note.content || '').length;
      return size > max ? size : max;
    }, 0);

    const totalStorageSize = notes.reduce((total, note) => {
      return total + (note.content || '').length + (note.title || '').length;
    }, 0);

    // Mock app opens this month
    const appOpensThisMonth = Math.floor(Math.random() * 50) + 20;

    // Mock reminder play count
    const reminderPlayCount = breaks.length * 30 + Math.floor(Math.random() * 100);

    return {
      notesThisWeek,
      lastNoteDate,
      largestNote,
      totalStorageSize,
      appOpensThisMonth,
      reminderPlayCount,
    };
  }, [notes, breaks]);

  const formatDate = (date) => {
    if (!date) return t('statistics.unknownDate');
    return new Date(date).toLocaleDateString(intlLocale, {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatBytes = (bytes) => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  const connectedDevices = useMemo(() => {
    // Mock device data - in real app, this would come from API
    return [
      { name: 'Windows PC', os: 'Windows 11', lastActive: new Date() },
      { name: 'iPhone', os: 'iOS 17', lastActive: new Date(Date.now() - 86400000) },
    ];
  }, []);

  const statCards = [
    {
      icon: TrendingUp,
      title: t('statistics.topFeature'),
      value: t('statistics.topFeatureNotes'),
      color: 'from-blue-500 to-cyan-500',
    },
    {
      icon: CalendarIcon,
      title: t('statistics.lastCreatedNote'),
      value: stats.lastNoteDate ? formatDate(stats.lastNoteDate) : t('statistics.noNotesYet'),
      color: 'from-purple-500 to-pink-500',
    },
    {
      icon: FileText,
      title: t('statistics.notesThisWeek'),
      value: t('statistics.notesCount', { count: stats.notesThisWeek }),
      color: 'from-green-500 to-emerald-500',
    },
    {
      icon: Activity,
      title: t('statistics.appOpensThisMonth'),
      value: t('statistics.timesCount', { count: stats.appOpensThisMonth }),
      color: 'from-orange-500 to-red-500',
    },
    {
      icon: FileCode,
      title: t('statistics.largestNote'),
      value: t('statistics.charactersCount', { count: stats.largestNote.toLocaleString(intlLocale) }),
      color: 'from-indigo-500 to-purple-500',
    },
    {
      icon: Bell,
      title: t('statistics.reminderPlays'),
      value: t('statistics.timesCount', { count: stats.reminderPlayCount }),
      color: 'from-yellow-500 to-amber-500',
    },
    {
      icon: HardDrive,
      title: t('statistics.storageUsage'),
      value: formatBytes(stats.totalStorageSize),
      color: 'from-teal-500 to-blue-500',
    },
  ];

  // Authentication check
  if (!isAuthenticated || !user || !user?.id) {
    return (
      <div className="w-full h-screen flex items-center justify-center bg-gradient-to-br from-gray-50 via-white to-gray-100 dark:from-dark-900 dark:via-dark-800 dark:to-dark-900">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="text-center"
        >
          <p className="text-gray-600 dark:text-gray-400 text-lg mb-4">
            {t('statistics.loginRequired')}
          </p>
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => navigate('/login')}
            className="px-6 py-3 bg-[#2563eb] hover:bg-[#3d8ef0] text-white rounded-lg font-medium transition-colors"
          >
            {t('statistics.signIn')}
          </motion.button>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="w-full min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-100 dark:from-dark-900 dark:via-dark-800 dark:to-dark-900 p-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 dark:text-white mb-2">
            {t('statistics.title')}
          </h1>
          <p className="text-gray-600 dark:text-gray-400">
            {t('statistics.subtitle')}
          </p>
        </motion.div>

        {/* Statistics Grid */}
        <div className="w-full mx-auto flex flex-col items-center">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 w-full">
            {statCards.map((card, index) => {
              const Icon = card.icon;
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.1 + index * 0.05, duration: 0.5 }}
                  whileHover={{ scale: 1.05, y: -5 }}
                  className="group relative backdrop-blur-xl bg-white/60 dark:bg-[var(--color-bg-elevated)]/60 rounded-2xl p-4 sm:p-5 lg:p-6 border border-white/20 dark:border-white/[0.07] shadow-lg hover:shadow-2xl transition-all duration-300 overflow-hidden"
                >
                  {/* Gradient Background on Hover */}
                  <div className={`absolute inset-0 bg-gradient-to-br ${card.color} opacity-0 group-hover:opacity-10 transition-opacity duration-300`} />
                  
                  {/* Icon */}
                  <div className={`relative mb-4 w-12 h-12 rounded-xl bg-gradient-to-br ${card.color} flex items-center justify-center shadow-lg`}>
                    <Icon className="w-6 h-6 text-white" />
                  </div>

                  {/* Content */}
                  <div className="relative">
                    <h3 className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-2">
                      {card.title}
                    </h3>
                    <p className="text-xl font-bold text-gray-900 dark:text-white">
                      {card.value}
                    </p>
                  </div>
                </motion.div>
              );
            })}

            {/* Connected Devices Card */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 + statCards.length * 0.05, duration: 0.5 }}
              whileHover={{ scale: 1.05, y: -5 }}
              className="group relative backdrop-blur-xl bg-white/60 dark:bg-[var(--color-bg-elevated)]/60 rounded-2xl p-4 sm:p-5 lg:p-6 border border-white/20 dark:border-white/[0.07] shadow-lg hover:shadow-2xl transition-all duration-300 overflow-hidden col-span-1 sm:col-span-2 lg:col-span-1"
            >
              <div className="absolute inset-0 bg-gradient-to-br from-cyan-500 to-blue-500 opacity-0 group-hover:opacity-10 transition-opacity duration-300" />
              
              <div className="relative mb-4 w-12 h-12 rounded-xl bg-gradient-to-br from-cyan-500 to-blue-500 flex items-center justify-center shadow-lg">
                <Smartphone className="w-6 h-6 text-white" />
              </div>

              <div className="relative">
                <h3 className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-4">
                  {t('statistics.connectedDevices')}
                </h3>
                <div className="space-y-3">
                  {connectedDevices.map((device, idx) => (
                    <div
                      key={idx}
                      className="p-3 rounded-lg bg-white/50 dark:bg-[#161f31] backdrop-blur-sm border border-white/20 dark:border-white/10/50"
                    >
                      <p className="text-sm font-semibold text-gray-900 dark:text-white">
                        {device.name}
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        {device.os} • {formatDate(device.lastActive)}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StatisticsPage;
